import { Component } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map } from "lodash"
import { CardContent, Paper, Tooltip as MuiTooltip } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import { PERIOD, DATE, RESOURCE_STATUS } from "helpers/shapes"
import { VIEW_TYPES } from "helpers/base"
import { computeCSVData } from "helpers/csv"
import { smallPercentageFormatter } from "helpers/formatters/numbers"
import AnalyzeHeader from "modules/analyze-header"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import NoData from "components/no-data"
import ForecastDetails from "modules/analyze-forecast-details"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import LoadingWrapper from "components/loading-wrapper"
import withNavigate from "hocs/withNavigate"

import "./style.scss"

class AnalyzeForecast extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    portfolioId: PropTypes.string.isRequired,
    base: PropTypes.object,
    earliestDate: DATE,
    latestDate: DATE,
    startDate: DATE,
    aiSummaryStartDate: DATE,
    endDate: DATE,
    selectedDate: DATE,
    period: PERIOD.isRequired,
    data: PropTypes.array.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    displayType: PropTypes.string.isRequired,
    viewType: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    setPeriod: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    setDisplayType: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    trackCSVDownload: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    fileName: PropTypes.string.isRequired,
    firstDate: PropTypes.string.isRequired,
    resetDate: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    selectedOption: PropTypes.object.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    params: PropTypes.object.isRequired,
    displayTypeName: PropTypes.string.isRequired,
    filtersVisible: PropTypes.bool.isRequired,
    forecast: PropTypes.object,
    adjusted: PropTypes.bool,
    currency: PropTypes.string,
    navigate: PropTypes.func.isRequired,
    passthroughResourcesStatus: RESOURCE_STATUS.isRequired,
  }

  static ACTIVE_TOOLTIP_MSG = `
    Although a comparison is currently provided,
    this portfolio's expected return is displayed in absolute terms.`

  componentDidMount() {
    const { forecast, account, portfolioId, navigate } = this.props
    if (!forecast) {
      setTimeout(() => navigate(`/${account}/p/${portfolioId}/analyze/trends/performance`, { replace: true }))
    }
  }

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate, resetDate } = this.props
    const startDate = firstDate || propsStartDate
    if (selectedDate && (selectedDate < startDate)) {
      resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setDisplayType } = this.props
    setDisplayType(id)
  }

  onToggleOnly = (factorId) => {
    const { toggleOnly, params: { forecastId } } = this.props
    toggleOnly(factorId, forecastId)
  }

  render() {
    const {
      earliestDate,
      latestDate,
      setDate,
      selectedDate,
      startDate,
      aiSummaryStartDate,
      endDate,
      period,
      data,
      items,
      displayType,
      options,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      viewType,
      isActive,
      fileName,
      resetDate,
      firstDate,
      selectedOption,
      trackCSVDownload,
      only,
      account,
      portfolioId,
      base,
      setPeriod,
      addNotification,
      displayTypeName,
      params: { forecastId },
      adjusted,
      currency,
      passthroughResourcesStatus,
    } = this.props

    let graph, rightPanel

    if (!earliestDate || !latestDate) {
      graph = <NoData title="Forecasts" className="graph" />
    }
    if (!data.length) {
      graph = <div className="components_charts_multi-line no-data">No securities in this portfolio was found in this forecast.</div>
    } else {
      graph = <MultiLine
        tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={smallPercentageFormatter} />}
        {...{
          tickFormat: smallPercentageFormatter,
          startDate,
          date: selectedDate,
          setDate,
          endDate,
          data,
          onlyItem: only,
          items,
          toggle,
          focusChartLine,
          blurChartLine,
          focusedChartLine,
          toggleOnly: this.onToggleOnly,
        }}
      />
      rightPanel = <ForecastDetails
        selectedDate={selectedDate}
        viewType={viewType}
        displayType={displayType}
        displayTypeName={displayTypeName}
        startDate={firstDate}
        endDate={endDate}
        selectedOption={selectedOption}
        forecastId={forecastId}
      />
    }

    return <div className="modules_analyze-forecast">
      <div className="historical-forecast-wrapper">
        <AnalyzeHeader
          selectedId={forecastId}
          {...{
            account,
            portfolioId,
            base,
            adjusted,
            earliestDate,
            latestDate,
            startDate,
            aiSummaryStartDate,
            endDate,
            selectedDate,
            period,
            setPeriod,
            addNotification,
            resetDate,
            firstDate,
            noExpansion: true,
            isActive,
            currency,
            changeDisplayPreferenceType: "navigation.analyze.trends",
          }}
        />
        <CardContent className={classnames("card-content")}>
          <Paper className="content">
            <div className="graph">
              <GraphTopBar {...{
                value: displayType,
                onTabChanged: this.onTabChanged,
                tabs: map(options, ({ id, label }) => ({
                  key: id,
                  value: id,
                  label,
                  tooltip: viewType === VIEW_TYPES.ACTIVE
                    ? <MuiTooltip title={AnalyzeForecast.ACTIVE_TOOLTIP_MSG}>
                      <OfflineBolt className="warning" />
                    </MuiTooltip> : null,
                })),
                actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackCSVDownload} />],
              }}
              />
              <LoadingWrapper status={[passthroughResourcesStatus]}>{graph}</LoadingWrapper>
            </div>
            {rightPanel}
          </Paper>
        </CardContent>
      </div>
    </div>
  }
}

export default withNavigate(AnalyzeForecast)
